import type { BillingUsage } from '@/api/types'

/** The D8 standing, in the owner's words. Null when there is nothing to say. */
export function standingNotice(
  billing: BillingUsage['billing'],
  formatInstant: (instant: string) => string,
): {
  readonly tone: 'info' | 'warning' | 'danger'
  readonly title: string
  readonly body: string
} | null {
  switch (billing.reason) {
    case 'billing_exempt':
      return {
        tone: 'info',
        title: 'Demo account',
        body: 'This is a demo account: it is never billed and is left out of revenue reporting. Plan limits still apply so they can be tested.',
      }
    case 'no_subscription':
      return {
        tone: 'info',
        title: 'Choose a plan',
        body: 'This workspace has no subscription yet, so it runs with the Start plan’s limits. Pick a plan below to lift them.',
      }
    case 'past_due_grace':
      return {
        tone: 'warning',
        title: 'Payment past due',
        body: `The last payment failed. Everything keeps working until ${
          billing.grace_ends_at === null
            ? 'the grace period ends'
            : formatInstant(billing.grace_ends_at)
        }; after that the workspace becomes read-only until the card is updated.`,
      }
    case 'past_due_expired':
    case 'canceled':
    case 'not_entitled':
      return {
        tone: 'danger',
        title: 'Workspace is read-only',
        body: 'Your data stays readable, but new records and non-essential texts are blocked until billing is restored. Replies to STOP and HELP still go through.',
      }
    default:
      return billing.access === 'read_only'
        ? {
            tone: 'danger',
            title: 'Workspace is read-only',
            body: 'Your data stays readable, but new records and non-essential texts are blocked until billing is restored.',
          }
        : null
  }
}

/** The SMS allowance notice the backend raised (80% once, 100% once per period). */
export function smsNotice(
  sms: BillingUsage['usage']['sms_segments'],
  periodEnd: string,
): { readonly tone: 'warning' | 'danger'; readonly title: string; readonly body: string } | null {
  if (sms.limit === null) return null
  const percent = sms.percent ?? (sms.limit === 0 ? 0 : (sms.used / sms.limit) * 100)
  if (percent >= 100) {
    return {
      tone: 'danger',
      title: 'SMS allowance used up',
      body: `Non-essential texts (offers, reminders, due-back outreach) are paused until ${periodEnd}. Confirmations, cancellations and STOP/HELP replies still send, and email continues. Upgrade to resume; nothing is charged automatically.`,
    }
  }
  if (percent >= 80) {
    return {
      tone: 'warning',
      title: `${String(Math.floor(percent))}% of this period’s SMS allowance used`,
      body: `At 100%, non-essential texts pause until ${periodEnd}. Email is not affected.`,
    }
  }
  return null
}
